import { useEffect, useRef, useState } from 'react';

import TagCloud from 'TagCloud';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faCode, faServer, faVial, faCloud, faRobot } from '@fortawesome/free-solid-svg-icons';

import { skillSections, SKILL_TAG_CLOUD_TEXTS } from '../../data';
import { decimalYearsSince } from '../../utils/yearsOfExperience';
import useInView from '../../hooks/useInView';
import Reveal from '../ui/Reveal';
import SectionHeading from '../ui/SectionHeading';

import '../../styles/Skill/Skill.scss';

const ICONS = [faStar, faRobot, faCode, faServer, faVial, faCloud];

/** Years of use, or null for skills listed without a duration. */
function yearsFor(item) {
  if (item.years != null) return item.years;
  return item.start ? decimalYearsSince(item.start) : null;
}

function formatYears(y) {
  const s = Number.isInteger(y) ? String(y) : y.toFixed(1);
  return `${s} ${parseFloat(s) === 1 ? 'yr' : 'yrs'}`;
}

const MAX_YEARS = Math.max(...skillSections.flatMap((s) => s.items.map(yearsFor)).filter((y) => y != null));

const cleanTitle = (t) => t.replace(/:$/, '');

function SkillPanel({ section, id, labelledBy }) {
  const [ref, inView] = useInView({ threshold: 0.1 });
  const items = section.items.filter((i) => yearsFor(i) != null).sort((a, b) => yearsFor(b) - yearsFor(a));
  const tags = section.items.filter((i) => yearsFor(i) == null);

  return (
    <div ref={ref} className="skill__panel" role="tabpanel" id={id} aria-labelledby={labelledBy}>
      <ul className="skill__bars">
        {items.map((item, i) => {
          const y = yearsFor(item);
          return (
            <li key={item.label} style={{ '--i': i }}>
              <div className="skill__bar-label">
                <span>{item.label}</span>
                <span className="skill__bar-years">{formatYears(y)}</span>
              </div>
              <div className="skill__bar-track" aria-hidden="true">
                <span style={{ transform: `scaleX(${inView ? y / MAX_YEARS : 0})` }} />
              </div>
            </li>
          );
        })}
      </ul>
      {tags.length > 0 && (
        <div className="skill__extra">
          <p>{items.length ? 'Also hands-on with' : 'Hands-on with'}</p>
          <ul>
            {tags.map((t, i) => (
              <li key={t.label} className="chip skill__chip" style={{ '--i': items.length + i }}>
                {t.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function tagCloudRadius() {
  const w = window.innerWidth;
  if (w <= 420) return 135;
  if (w <= 778) return 160;
  return 230;
}

const Skill = () => {
  const [active, setActive] = useState(0);
  const tabRefs = useRef([]);
  const cloudRef = useRef(null);

  useEffect(() => {
    const el = cloudRef.current;
    if (!el) return undefined;
    let instance = TagCloud(el, SKILL_TAG_CLOUD_TEXTS, {
      radius: tagCloudRadius(),
      maxSpeed: 'normal',
      initSpeed: 'normal',
      keep: true,
    });

    let lastRadius = tagCloudRadius();
    const onResize = () => {
      const r = tagCloudRadius();
      if (r === lastRadius) return;
      lastRadius = r;
      if (instance && typeof instance.destroy === 'function') instance.destroy();
      instance = TagCloud(el, SKILL_TAG_CLOUD_TEXTS, { radius: r, maxSpeed: 'normal', initSpeed: 'normal', keep: true });
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (instance && typeof instance.destroy === 'function') instance.destroy();
    };
  }, []);

  const onKeyDown = (e) => {
    const n = skillSections.length;
    let next = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (active + 1) % n;
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (active - 1 + n) % n;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = n - 1;
    if (next !== null) {
      e.preventDefault();
      setActive(next);
      tabRefs.current[next]?.focus();
    }
  };

  const totalSkills = skillSections.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <section className="section skill" id="skills" aria-labelledby="skills-title">
      <div className="container">
        <SectionHeading
          index="05"
          eyebrow="Skills & tools"
          id="skills-title"
          title="Focused stack,"
          accent="fast learner."
          description={`My core is React, Angular, React Native, Next.js and Node.js, plus ${totalSkills - skillSections[0].items.length} more technologies I've used in production. I pick up new tools quickly and can be productive in any stack.`}
        />

        <div className="skill__layout">
          <Reveal className="skill__explorer" variant="left">
            <div className="skill__tabs" role="tablist" aria-label="Skill categories" aria-orientation="vertical">
              {skillSections.map((s, i) => (
                <button
                  key={s.title}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`skill-tab-${i}`}
                  aria-selected={active === i}
                  aria-controls={`skill-panel-${i}`}
                  tabIndex={active === i ? 0 : -1}
                  className={`skill__tab${active === i ? ' is-active' : ''}`}
                  onClick={() => setActive(i)}
                  onKeyDown={onKeyDown}
                >
                  <FontAwesomeIcon icon={ICONS[i % ICONS.length]} aria-hidden="true" />
                  <span>{cleanTitle(s.title)}</span>
                  <small>{s.items.length}</small>
                </button>
              ))}
            </div>
            <SkillPanel
              key={active}
              section={skillSections[active]}
              id={`skill-panel-${active}`}
              labelledBy={`skill-tab-${active}`}
            />
          </Reveal>

          <Reveal className="skill__sphere" variant="zoom" delay={150}>
            <div className="skill__sphere-glow" aria-hidden="true" />
            <div className="skill__cloud" ref={cloudRef} aria-hidden="true" />
            <p className="skill__sphere-hint">Drag to spin</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Skill;
